import { useShopI18n } from '../../i18n'
import type { CheckoutSavedAddress } from '../addressBookAdapter'
import type { CheckoutAddressForm } from '../flow'
import { CheckoutAddressPanel } from './CheckoutAddressPanel'
import { SavedAddressCards } from './SavedAddressCards'
import { useCheckoutAddressPanel } from './useCheckoutAddressPanel'

type Props = {
  form: CheckoutAddressForm
  validationByField: Map<string, string>
  sameAsShipping: boolean
  savedAddresses: CheckoutSavedAddress[]
  disabled: boolean
  onFieldChange: (field: keyof CheckoutAddressForm, value: string) => void
  onSetForm: (value: CheckoutAddressForm) => void
  onSameAsShippingChange: (value: boolean) => void
  onSubmit: () => void
  onBack: () => void
}

export const ShippingAddressStep = ({
  form,
  validationByField,
  sameAsShipping,
  savedAddresses,
  disabled,
  onFieldChange,
  onSetForm,
  onSameAsShippingChange,
  onSubmit,
  onBack,
}: Props) => {
  const { t } = useShopI18n()
  const addressPanel = useCheckoutAddressPanel({
    customAddress: {
      defaultBilling: false,
      defaultShipping: false,
      idPrefix: 'shipping-custom',
      label: 'Shipping',
      useBilling: false,
      useShipping: true,
    },
    form,
    missingFieldsMessage: t('checkout.addressBook.panelMissingFields'),
    onSetForm,
    savedAddresses,
    selectSavedAddress: (entry) => entry.useShipping || entry.defaultShipping,
  })

  return (
    <section className="space-y-5 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm md:p-6">
      <header>
        <h2 className="text-xl font-semibold text-slate-900">{t('checkout.shippingAddress.title')}</h2>
        <p className="mt-1 text-sm text-slate-600">{t('checkout.shippingAddress.description')}</p>
      </header>

      <SavedAddressCards
        groupLabel={t('checkout.shippingAddress.title')}
        addresses={addressPanel.addresses}
        selectedAddressId={addressPanel.selectedAddressId}
        disabled={disabled}
        onSelectAddress={addressPanel.selectAddress}
        onEditAddress={addressPanel.editAddress}
        onAddNewAddress={addressPanel.addNewAddress}
      />

      {addressPanel.isFormPanelOpen ? (
        <CheckoutAddressPanel
          disabled={disabled}
          editingAddressId={addressPanel.editingAddressId}
          form={form}
          formIdPrefix="checkout-shipping"
          onCancel={addressPanel.cancelPanelAddress}
          onFieldChange={onFieldChange}
          onSubmit={addressPanel.submitPanelAddress}
          panelError={addressPanel.panelError}
          validationByField={validationByField}
        />
      ) : null}

      <label className="flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700">
        <input
          type="checkbox"
          checked={sameAsShipping}
          disabled={disabled}
          onChange={(event) => onSameAsShippingChange(event.target.checked)}
        />
        <span>{t('checkout.billing.sameAsShipping')}</span>
      </label>

      <div className="flex items-center justify-between">
        <button
          type="button"
          className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-500"
          onClick={onBack}
        >
          {t('checkout.cta.back')}
        </button>
        <button
          type="button"
          className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white transition hover:bg-slate-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-500 disabled:cursor-not-allowed disabled:opacity-50"
          disabled={disabled}
          onClick={onSubmit}
        >
          {t('checkout.cta.saveAndContinue')}
        </button>
      </div>
    </section>
  )
}
